import {
  AGREEMENT_STATUS_DRAFT,
  AGREEMENT_STATUS_SENT,
  AGREEMENT_STATUS_SIGNED,
} from '@src/components/sco/Advisors/utils/agreementKeys'
import { cleanErrorMessage } from '@src/components/sco/Advisors/utils/sessionFormat'

/** Drafts need both parties before `sendAdvisorAgreement`. */
export const SEND_PARTY_REQUIRED = "Add the advisor's email before sending."

export const SEND_FOUNDER_REQUIRED = "Add the founder's email before sending."

export const SEND_NOT_DRAFT = 'Only drafts can be sent.'

export const SEND_CANNOT_EDIT = 'You cannot send this agreement.'

const SEND_ERROR_COPY: Record<string, string> = {
  ADVISOR_AGREEMENT_PARTY_REQUIRED: SEND_PARTY_REQUIRED,
  ADVISOR_AGREEMENT_FOUNDER_REQUIRED: SEND_FOUNDER_REQUIRED,
  ADVISOR_AGREEMENT_INVALID_TRANSITION: 'This agreement is not a draft anymore.',
  ADVISOR_AGREEMENT_NOT_EDITABLE: 'Only drafts can be edited or sent.',
  USER_NOT_AUTHORIZED: 'You cannot send this agreement.',
}

export type AgreementSendContext = {
  statusKey?: string | null
  currentUserCanEdit?: boolean | null
  initiatingPartyKey?: string | null
  advisorUserId?: string | null
  advisorEmail?: string | null
  founderUserId?: string | null
  founderEmail?: string | null
}

export const advisorPartyForSend = (ctx: Pick<AgreementSendContext, 'advisorUserId' | 'advisorEmail'>) => {
  const advisorUserId = ctx.advisorUserId?.trim() || null
  const advisorEmail = ctx.advisorEmail?.trim() || null
  return {
    advisorUserId,
    advisorEmail,
    hasParty: Boolean(advisorUserId || advisorEmail),
  }
}

/** Why Send is blocked, or null when the draft can go out. */
export const sendAdvisorAgreementBlockedReason = (ctx: AgreementSendContext): string | null => {
  if (ctx.statusKey !== AGREEMENT_STATUS_DRAFT) {
    return SEND_NOT_DRAFT
  }
  if (ctx.currentUserCanEdit === false) {
    return SEND_CANNOT_EDIT
  }
  if (!advisorPartyForSend(ctx).hasParty) {
    return SEND_PARTY_REQUIRED
  }
  if (!ctx.founderUserId?.trim() && !ctx.founderEmail?.trim()) {
    return SEND_FOUNDER_REQUIRED
  }
  return null
}

export const canSendAdvisorAgreement = (ctx: AgreementSendContext): boolean =>
  sendAdvisorAgreementBlockedReason(ctx) == null

export const agreementSendErrorMessage = (error: unknown, fallback: string): string => {
  const raw = cleanErrorMessage(error, fallback)
  return SEND_ERROR_COPY[raw] || raw
}

export const isAgreementAwaitingMarkSigned = (statusKey?: string | null): boolean =>
  statusKey === AGREEMENT_STATUS_SENT

export const DELETE_UNSIGNED_AGREEMENT_CONFIRM =
  'Delete this FAST agreement? Only unsigned drafts, sent, or declined terms can be removed. This cannot be undone.'

export const canDeleteUnsignedAdvisorAgreement = (ctx: {
  statusKey?: string | null
  currentUserRole?: string | null
  currentUserCanDelete?: boolean | null
}): boolean => {
  if (!ctx.statusKey || ctx.statusKey === AGREEMENT_STATUS_SIGNED) {
    return false
  }
  if (ctx.currentUserCanDelete != null) {
    return Boolean(ctx.currentUserCanDelete)
  }
  return ctx.currentUserRole === 'founder'
}
